import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Avatar,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
} from '@mui/material';
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt';
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt';
import ReplyOutlinedIcon from '@mui/icons-material/ReplyOutlined';
import { Editor } from '@tinymce/tinymce-react';
import { styled } from '@mui/material/styles';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import {
  fetchThread,
  addComment,
  upvoteComment,
  downvoteComment,
  upvoteThread,
  downvoteThread,
  deleteThread,
  lockThread,
  removeThread,
  fetchThreadsBySubtopic,
  fetchUserThreads,
  deleteComment as deleteCommentApi,
  lockComment,
  removeComment,
  reportThread,
} from '../services/api';
import { Alert, Snackbar } from "@mui/material"; // Import MUI Alert & Snackbar
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FeedIcon from '@mui/icons-material/Feed';
import FaceIcon from '@mui/icons-material/Face';
import AddIcon from '@mui/icons-material/Add';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { useTheme, useMediaQuery } from '@mui/material';
import { useInstitution } from '../components/InstitutionContext';
import { userHasInstitutionRole } from '../utils/permissions';
import ThreadListSidebar from '../components/ThreadListSidebar';
import RightSidebarContainer from '../components/RightSidebarContainer';
import CollapsibleSidebarSection from '../components/CollapsibleSidebarSection';
import {
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
} from '@mui/material';
import { Paper } from '@mui/material';
import ThumbUp from '@mui/icons-material/ThumbUp';
import ThumbDown from '@mui/icons-material/ThumbDown';
import IconTextChip from '../components/IconTextChip';
import IconOnlyChip from '../components/IconOnlyChip';
import { hexToRGBA } from '../utils/colors';
import { useAuth } from '../components/AuthContext';
import { Link as RouterLink } from 'react-router-dom';
import getUserDisplayName from '../utils/getUserDisplayName';
import { isThreadEdited } from '../utils/isEdited';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EditThreadDialog from '../components/EditThreadDialog';
import EditCommentDialog from '../components/EditCommentDialog';
import ReplyThread from '../components/ReplyThread';
import CommentSearchBar from '../components/CommentSearchBar';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import FlagIcon from '@mui/icons-material/Flag';
import OutlinedFlagIcon from '@mui/icons-material/OutlinedFlag';
import ActionReasonDialog from '../components/ActionReasonDialog';
import FilledButton from '../components/FilledButton';
import LoginPromptAlert from '../components/LoginPromptAlert';
import VerifyEmailPromptAlert from '../components/VerifyEmailPromptAlert';
import { POST_LOCK_REASONS, POST_ADMIN_DECISION_REASONS, POST_REPORT_REASONS } from '../utils/moderationReasons';
import InfiniteScroll from 'react-infinite-scroll-component';
import { CircularProgress } from '@mui/material';
import EditedTag from '../components/EditedTag';
import AddPostDialog from '../components/AddPostDialog';

// Styled Components
const CommentContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

const CommentHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const CommentContent = styled(Box)(({ theme }) => ({
  marginLeft: theme.spacing(4),
  marginTop: theme.spacing(1),
  paddingRight: theme.spacing(1),
  marginBottom: theme.spacing(2),
}));

const CommentActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  marginTop: theme.spacing(-1),
  marginLeft: theme.spacing(4),
}));



const TitleTypography = styled(Typography)(({ theme }) => ({
  position: 'relative',
  textDecoration: 'none',
  '&:hover': { cursor: 'pointer' },
  '&:focus-visible': {
    outline: '3px solid',
    outlineColor: 'hsla(210, 98%, 48%, 0.5)',
    outlineOffset: '3px',
    borderRadius: '8px',
  },
}));

const formatDateTime = (isoString) => {
  const date = new Date(isoString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
    timeZoneName: 'short',
  });
};

const buildCommentTree = (comments) => {
  const map = new Map();
  const roots = [];
  (comments || []).forEach((c) => {
    map.set(c.id, { ...c, replies: [] });
  });
  map.forEach((node) => {
    if (node.parent_id && map.has(node.parent_id)) {
      map.get(node.parent_id).replies.push(node);
    } else {
      roots.push(node);
    }
  });
  const sortTree = (nodes) => {
    nodes.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    nodes.forEach((n) => sortTree(n.replies));
  };
  sortTree(roots);
  return roots;
};

const filterCommentTree = (comments, query) => {
  if (!query) return comments;
  const q = query.toLowerCase();

  const filterRecursive = (nodes) => {
    const result = [];
    nodes.forEach((node) => {
      const text = (node.content || '').toLowerCase();
      const childMatches = filterRecursive(node.replies || []);
      const isTopLevel = !node.parent_id;
      const isMatch = text.includes(q);

      if (isTopLevel && isMatch) {
        result.push({ ...node, replies: [] });
      } else if (isMatch) {
        result.push({ ...node, replies: childMatches });
      } else if (childMatches.length > 0) {
        result.push({ ...node, replies: childMatches });
      }
    });
    return result;
  };

  return filterRecursive(comments);
};

const sortComments = (nodes, sortBy) => {
  const sorted = [...nodes];
  if (sortBy === 'oldest') {
    sorted.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  } else if (sortBy === 'upvotes') {
    sorted.sort((a, b) => (b.upvotes - b.downvotes) - (a.upvotes - a.downvotes));
  } else {
    sorted.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }
  return sorted;
};


export default function ThreadPage() {
  const { threadId } = useParams();
  const [threadData, setThreadData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAddingComment, setIsAddingComment] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [replyingTo, setReplyingTo] = useState(null); // Tracks the comment being replied to
  const [replyContent, setReplyContent] = useState(''); // Holds reply text
  const [visibleCount, setVisibleCount] = useState(5);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const navigate = useNavigate();
  const { user, emailVerified, loading: authLoading } = useAuth();
  const verifiedUser = user && emailVerified;
  const unverifiedUser = user && !emailVerified;

  const { selectedInstitution } = useInstitution();
  const canModerate = userHasInstitutionRole(user, 'CommunityAdminTeam', selectedInstitution);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  const [relatedThreads, setRelatedThreads] = useState([]);
  const [userThreads, setUserThreads] = useState([]);
  const [postOpen, setPostOpen] = useState(false);
  const [threadUpvotes, setThreadUpvotes] = useState(0);
  const [threadDownvotes, setThreadDownvotes] = useState(0);
  const [threadUpvoted, setThreadUpvoted] = useState(false);
  const [threadDownvoted, setThreadDownvoted] = useState(false);
  const [threadReported, setThreadReported] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const isAuthor = user?.id === threadData?.thread?.created_by?.id;

  const [editCommentData, setEditCommentData] = useState(null);
  const [deleteCommentData, setDeleteCommentData] = useState(null);
  const [lockThreadOpen, setLockThreadOpen] = useState(false);
  const [removeThreadOpen, setRemoveThreadOpen] = useState(false);
  const [reportThreadOpen, setReportThreadOpen] = useState(false);
  const [lockCommentData, setLockCommentData] = useState(null);
  const [removeCommentData, setRemoveCommentData] = useState(null);

  const location = useLocation();
  const previousPath = location.state?.from || '/community';
  const [expandedComments, setExpandedComments] = useState({});

  const toggleExpanded = (commentId) => {
    setExpandedComments((prev) => ({
      ...prev,
      [commentId]: !prev[commentId],
    }));
  };

  const fetchMoreComments = () => {
    setTimeout(() => {
      setVisibleCount((prev) => prev + 5);
    }, 500);
  };

  useEffect(() => {
    setVisibleCount(5);
  }, [searchQuery, sortBy]);
  
  useEffect(() => {
    if (authLoading) return; // wait until auth state resolves
  
    const loadThread = async () => {
      try {
        const data = await fetchThread(threadId);

        setThreadData(data);
        setExpandedComments({});
        setThreadUpvotes(data.thread.upvotes || 0);
        setThreadDownvotes(data.thread.downvotes || 0);
        setThreadUpvoted(data.thread.upvoted || false);
        setThreadDownvoted(data.thread.downvoted || false);
        setThreadReported(data.thread.reported || false);

        if (data.thread?.subtopic?.id) {
          const subtopicThreads = await fetchThreadsBySubtopic(
            data.thread.subtopic.id,
            selectedInstitution
          );
          const filtered = (subtopicThreads || [])
            .filter(t => t.id !== data.thread.id)
            .slice(0, 5);
          setRelatedThreads(filtered);
        } else {
          setRelatedThreads([]);
        }

        const userThreadsData = await fetchUserThreads(
          data.thread.created_by.display_name,
          selectedInstitution
        );
        const userFiltered = (userThreadsData || [])
          .filter(t => t.id !== data.thread.id)
          .slice(0, 5);
        setUserThreads(userFiltered);
  
      } catch (err) {
        console.error(err);
        setErrorMessage("Failed to load thread.");
      } finally {
        setLoading(false);
      }
    };
  
    loadThread();
  }, [threadId, user, selectedInstitution, authLoading]);
  

  const handleUpvote = async (commentId) => {
    try {
      const res = await upvoteComment(commentId);
      const updated = res.data;
  
      setThreadData((prev) => ({
        ...prev,
        comments: prev.comments.map((c) =>
          c.id === commentId ? { ...c, ...updated } : c
        ),
      }));
    } catch (err) {
      console.error("Upvote error:", err);
      setErrorMessage("Failed to upvote.");
    }
  };
  
  const handleDownvote = async (commentId) => {
    try {
      const res = await downvoteComment(commentId);
      const updated = res.data;
  
      setThreadData((prev) => ({
        ...prev,
        comments: prev.comments.map((c) =>
          c.id === commentId ? { ...c, ...updated } : c
        ),
      }));
    } catch (err) {
      console.error("Downvote error:", err);
      setErrorMessage("Failed to downvote.");
    }
  };

  const handleThreadUpvote = async () => {
    try {
      const res = await upvoteThread(threadId);
      const { upvotes, downvotes, upvoted, downvoted } = res.data;
      setThreadUpvotes(upvotes);
      setThreadDownvotes(downvotes);
      setThreadUpvoted(upvoted);
      setThreadDownvoted(downvoted);
    } catch (err) {
      console.error("Thread upvote error:", err);
      setErrorMessage("Failed to upvote thread.");
    }
  };

  const handleThreadDownvote = async () => {
    try {
      const res = await downvoteThread(threadId);
      const { upvotes, downvotes, upvoted, downvoted } = res.data;
      setThreadUpvotes(upvotes);
      setThreadDownvotes(downvotes);
      setThreadUpvoted(upvoted);
      setThreadDownvoted(downvoted);
    } catch (err) {
      console.error("Thread downvote error:", err);
      setErrorMessage("Failed to downvote thread.");
    }
  };

  const handleAuthorClick = (e) => {
    e.stopPropagation();
    if (!threadData?.thread) return;
    const name = getUserDisplayName(threadData.thread.created_by);
    if (!name) return;
    const path = `/community/user/${encodeURIComponent(name)}`;
    if (location.pathname !== path) {
      navigate(path, { state: { from: location.pathname } });
    }
  };

  const handleDeleteThread = async () => {
    try {
      await deleteThread(threadId);
      setSuccessMessage('Thread deleted.');
      setTimeout(() => setSuccessMessage(''), 3000);
      navigate(previousPath);
    } catch (err) {
      console.error('Failed to delete thread', err);
      setErrorMessage('Failed to delete thread.');
    } finally {
      setConfirmOpen(false);
    }
  };

  const handleDeleteComment = async () => {
    if (!deleteCommentData) return;
    try {
      await deleteCommentApi(deleteCommentData.id);
      setThreadData(prev => ({
        ...prev,
        comments: prev.comments.filter(c => c.id !== deleteCommentData.id),
      }));
      setSuccessMessage('Comment deleted.');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Failed to delete comment', err);
      setErrorMessage('Failed to delete comment.');
    } finally {
      setDeleteCommentData(null);
    }
  };

  const handleCommentUpdated = (id, content) => {
    setThreadData(prev => ({
      ...prev,
      comments: prev.comments.map(c =>
        c.id === id ? { ...c, content } : c
      ),
    }));
  };

  const handleLockThreadConfirm = async (reason) => {
    try {
      await lockThread(threadId, reason);
      setThreadData(prev => ({
        ...prev,
        thread: { ...prev.thread, is_locked: true, lock_reason: reason },
      }));
      setSuccessMessage('Thread locked.');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Failed to lock thread', err);
      setErrorMessage('Failed to lock thread.');
    } finally {
      setLockThreadOpen(false);
    }
  };

  const handleUnlockThread = async () => {
    try {
      await lockThread(threadId);
      setThreadData(prev => ({
        ...prev,
        thread: { ...prev.thread, is_locked: false, lock_reason: '' },
      }));
      setSuccessMessage('Thread unlocked.');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Failed to unlock thread', err);
      setErrorMessage('Failed to unlock thread.');
    }
  };

  const handleRemoveThreadConfirm = async (reason) => {
    try {
      await removeThread(threadId, reason);
      setThreadData(prev => ({
        ...prev,
        thread: {
          ...prev.thread,
          is_removed: true,
          deletion_reason: reason,
          title: '[removed]',
          content: '[removed]',
        },
      }));
      setSuccessMessage('Thread removed.');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Failed to remove thread', err);
      setErrorMessage('Failed to remove thread.');
    } finally {
      setRemoveThreadOpen(false);
    }
  };

  const handleReportThreadConfirm = async (reason) => {
    try {
      const res = await reportThread(threadId, reason);
      setThreadData(prev => ({ ...prev, thread: { ...prev.thread, ...res } }));
      setThreadReported(true);
      setSuccessMessage('Thread reported.');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Failed to report thread', err);
      setErrorMessage('Failed to report thread.');
    } finally {
      setReportThreadOpen(false);
    }
  };

  const handleLockCommentConfirm = async (reason) => {
    if (!lockCommentData) return;
    try {
      await lockComment(lockCommentData.id, reason);
      setThreadData(prev => ({
        ...prev,
        comments: prev.comments.map(c =>
          c.id === lockCommentData.id ? { ...c, is_locked: true, lock_reason: reason } : c
        ),
      }));
      setSuccessMessage('Comment locked.');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Failed to lock comment', err);
      setErrorMessage('Failed to lock comment.');
    } finally {
      setLockCommentData(null);
    }
  };

  const handleUnlockComment = async (comment) => {
    try {
      await lockComment(comment.id);
      setThreadData(prev => ({
        ...prev,
        comments: prev.comments.map(c =>
          c.id === comment.id ? { ...c, is_locked: false, lock_reason: '' } : c
        ),
      }));
      setSuccessMessage('Comment unlocked.');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Failed to unlock comment', err);
      setErrorMessage('Failed to unlock comment.');
    }
  };

  const handleRemoveCommentConfirm = async (reason) => {
    if (!removeCommentData) return;
    try {
      await removeComment(removeCommentData.id, reason);
      setThreadData(prev => ({
        ...prev,
        comments: prev.comments.map(c =>
          c.id === removeCommentData.id
            ? { ...c, is_removed: true, deletion_reason: reason, content: '[removed]', user: null }
            : c
        ),
      }));
      setSuccessMessage('Comment removed.');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Failed to remove comment', err);
      setErrorMessage('Failed to remove comment.');
    } finally {
      setRemoveCommentData(null);
    }
  };

  
  const handleAddComment = async () => {
    if (!newComment.trim() && !replyContent.trim()) return;
  
    try {
      const contentToSubmit = replyingTo
        ? replyContent
        : newComment;
    
      const commentData = await addComment(
        threadId,
        contentToSubmit,
        replyingTo?.id || null
      );

      if (!commentData || !commentData.id) {
        console.error("Error: API did not return a valid comment object.");
        return;
      }
  
      // Ensure correct user data
      const user = {
        id: commentData.created_by?.id || "Unknown",
        name:
          commentData.created_by?.display_name ||
          commentData.created_by?.username ||
          "Anonymous",
        avatar: commentData.created_by?.avatar || "",
      };
  
      // Construct new comment object
      const newCommentData = {
        id: commentData.id,
        content: commentData.content,
        user: user,
        created_at: commentData.created_at || new Date().toISOString(),
        parent_id: commentData.parent_id,
        top_level_parent_id: commentData.top_level_parent_id || commentData.id,
        upvotes: 0,
        downvotes: 0,
        upvoted: commentData.upvoted ?? false,
        downvoted: commentData.downvoted ?? false,
        replies: [],
      };
    
      setThreadData((prev) => {
        if (!prev) return prev;
    
        let updatedComments = JSON.parse(JSON.stringify(prev.comments)); // Deep copy    
        if (replyingTo) {
            // Find the top-level parent ID
            const topLevelId = replyingTo.top_level_parent_id || replyingTo.id;
    
            // Ensure the reply is placed as a sibling under the correct top-level comment
            let updatedReplies = [...updatedComments, newCommentData];    
            updatedComments = updatedReplies;
        } else {
            updatedComments = [...updatedComments, newCommentData];
        }
    
    
        return { ...prev, comments: updatedComments }; // Ensure React state updates
    });
    
      
  
      setSuccessMessage("Comment posted successfully!");
      setNewComment("");
      setReplyContent("");
      setReplyingTo(null);
      setIsAddingComment(false);
  
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      console.error("Failed to add comment:", err);
      setErrorMessage("Failed to add comment.");
    }
  };
  
  
  
  
  
  if (loading) return <Typography>Loading thread...</Typography>;
  if (!threadData?.thread) return <Typography>Error: Thread not found.</Typography>;

  const { thread, comments } = threadData;
  const threadDeleted = thread.is_deleted;
  const threadLocked = thread.is_locked;
  const threadRemoved = thread.is_removed;
  const commentTree = buildCommentTree(comments);
  const totalComments = commentTree.length;
  const filteredTree = filterCommentTree(commentTree, searchQuery);
  const sortedComments = sortComments(filteredTree, sortBy);
  // console.log("Thread topic color:", thread);

  return (
    <Box
      sx={{ maxWidth: '1200px', width: '100%', mx: 'auto', py: 3 }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
        <Box sx={{ flexGrow: 1, px: 4 }}>
          <Box sx={{ mb: 4, mt: 5 }}>
            <Button
        variant="outlined"
        startIcon={<ArrowBackIcon color="success" />}
        onClick={() => navigate(previousPath)}
        sx={{ mb: 2 }}
      >
        Back
      </Button>

            {isSmallScreen && thread.topic && (
              <>
                {relatedThreads.length > 0 && (
                  <CollapsibleSidebarSection
                    title={`More on ${thread.subtopic.name}`}
                    icon={<FeedIcon fontSize="small" color="success" />}
                  >
                    <ThreadListSidebar
                      title={`More on ${thread.subtopic.name}`}
                      threads={relatedThreads}
                      currentThreadId={thread.id}
                      Icon={FeedIcon}
                    />
                  </CollapsibleSidebarSection>
                )}
                {userThreads.length > 0 && (
                  <CollapsibleSidebarSection
                    title={`More by ${thread.created_by.display_name || thread.created_by.username}`}
                    icon={<FaceIcon fontSize="small" color="success" />}
                  >
                    <ThreadListSidebar
                      title={`More by ${thread.created_by.display_name || thread.created_by.username}`}
                      threads={userThreads}
                      currentThreadId={thread.id}
                      Icon={FaceIcon}
                    />
                  </CollapsibleSidebarSection>
                )}
                {verifiedUser && (
                  <Button
                    component={RouterLink}
                  to={`/community/user/${encodeURIComponent(
                      getUserDisplayName(user)
                    )}`}
                    variant="outlined"
                    size="large"
                    sx={{ mb: 1 }}
                  >
                    View My Posts
                  </Button>
                )}
                {verifiedUser ? (
                  <FilledButton
                    onClick={() => setPostOpen(true)}
                    text="Add a Post"
                    color="success"
                    size="large"
                    sx={{ width: '100%', mb: 2 }}
                    data-tour-id="community-add-post"
                  />
                ) : !user ? (
                  <LoginPromptAlert
                    from={location.pathname}
                    message="to Add a Post."
                    sx={{ width: '100%', mb: 2 }}
                  />
                ) : (
                  <VerifyEmailPromptAlert
                    message="to Add a Post."
                    sx={{ width: '100%', mb: 2 }}
                  />
                )}
              </>
            )}



        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Avatar
            src={thread.created_by?.avatar || undefined}
            alt={
              thread.created_by?.display_name ||
              thread.created_by?.username ||
              'Anonymous'
            }
            sx={{ width: 30, height: 30, cursor: 'pointer' }}
            onClick={handleAuthorClick}
          >
            {!thread.created_by?.avatar &&
              (thread.created_by?.display_name ||
                thread.created_by?.username ||
                'A')[0]}
          </Avatar>
          <Typography
            onClick={handleAuthorClick}
            sx={{ fontWeight: 'bold', cursor: 'pointer' }}
          >
            {thread.created_by?.display_name ||
              thread.created_by?.username ||
              'Anonymous'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {formatDateTime(thread.created_at)}
          </Typography>
        </Box>

            <TitleTypography variant="h4" sx={{ my: 1 }}>
              {thread.title}
            </TitleTypography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
          {thread.topic && (
            <Chip
              label={thread.topic.name}
              size="small"
              sx={{
                fontWeight: 'bold',
                border: `2px solid ${thread.topic.color}`,
                backgroundColor: `${thread.topic.color}20`,
                color: thread.topic.color
              }}
            />
          )}
          {thread.subtopic && (
            <Chip
              label={thread.subtopic.name}
              size="small"
              sx={{
                backgroundColor: (theme) => theme.palette.action.hover,
                color: (theme) => theme.palette.text.primary,
                fontWeight: 500,
                fontSize: '0.75rem',
                height: '20px'
              }}
            />
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}>
            {user?.permissions?.includes('can_lock_post') && canModerate && (
              <IconTextChip
                icon={threadLocked ? <LockOpenIcon /> : <LockIcon />}
                label={threadLocked ? 'Unlock' : 'Lock'}
                color="warning"
                onClick={() =>
                  threadLocked ? handleUnlockThread() : setLockThreadOpen(true)
                }
              />
            )}
            {user?.permissions?.includes('can_remove_post') && canModerate && !threadRemoved && (
              <IconTextChip
                icon={<RemoveCircleIcon />}
                label="Remove"
                color="error"
                onClick={() => setRemoveThreadOpen(true)}
              />
            )}
            <IconTextChip
              icon={threadReported ? <FlagIcon /> : <OutlinedFlagIcon />}
              label={threadReported ? 'Reported' : 'Report'}
              color="error"
              onClick={user ? () => setReportThreadOpen(true) : undefined}
              disabled={threadReported || !verifiedUser}
            />
          </Box>
        </Box>

        <Paper
          elevation={1}
          sx={{
            p: 2,
            borderRadius: 2,
          }}
        >
          <Typography
            variant="body1"
            sx={{
              lineHeight: 1.6, // or adjust to 1.4–1.5 based on your font
              p: 0,
              m: 0,
              marginTop: -1,
              '& p:last-child': {
                marginBottom: 0, // Prevent bottom margin from last paragraph
              }
            }}
            dangerouslySetInnerHTML={{ __html: thread.content }}
          />
          {isThreadEdited(thread) &&
            !editOpen &&
            !['[deleted]', '[removed]'].includes((thread.content || '').trim()) && (
              <EditedTag style={{ marginLeft: 4 }} />
            )}
        </Paper>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 2 }}>
          {thread.view_count !== undefined && (
            <IconTextChip
              icon={<VisibilityOutlinedIcon />}
              label={thread.view_count}
            />
          )}
          <IconTextChip
            icon={threadUpvoted ? <ThumbUp /> : <ThumbUpOffAltIcon />}
            label={threadUpvotes}
            color="success"
            onClick={verifiedUser ? handleThreadUpvote : null}
            disabled={!verifiedUser || threadDeleted || threadLocked || threadRemoved}
          />
          <IconTextChip
            icon={threadDownvoted ? <ThumbDown /> : <ThumbDownOffAltIcon />}
            label={threadDownvotes}
            color="success"
            onClick={verifiedUser ? handleThreadDownvote : null}
            disabled={!verifiedUser || threadDeleted || threadLocked || threadRemoved}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}>
            {isAuthor && (
              <>
                <IconOnlyChip
                  icon={<EditOutlinedIcon />}
                  color="success"
                  onClick={() => setEditOpen(true)}
                  disabled={threadDeleted || threadLocked || threadRemoved}
                />
                <IconOnlyChip
                  icon={<DeleteOutlineOutlinedIcon />}
                  color="error"
                  onClick={() => setConfirmOpen(true)}
                  disabled={threadDeleted || threadLocked || threadRemoved}
                />
              </>
            )}
          </Box>
        </Box>
        {verifiedUser && !isAddingComment && (
          <Button
            variant="outlined"
            color="success"
            startIcon={<AddIcon />}
            sx={{ mt: 1 }}
            onClick={() => setIsAddingComment(true)}
            disabled={threadDeleted || threadLocked || threadRemoved}
          >
            Comment
          </Button>
        )}


        </Box>
        {user ? (
          isAddingComment && !threadLocked && !threadRemoved ? (
            <>
              <Editor
                      tinymceScriptSrc="/tinymce/tinymce.min.js"
                      value={newComment}
                      init={{
                        license_key: 'gpl',
                        height: 200,
                        menubar: false,
                        plugins: ['link', 'lists', 'autolink', 'charmap', 'table'],
                        toolbar: 'undo redo | bold italic underline | alignleft aligncenter alignright | bullist numlist outdent indent',
                      }}
                      onEditorChange={(content) => setNewComment(content)}
                    />
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Button onClick={handleAddComment} variant="contained" color="primary">
                  Submit
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => {
                    setIsAddingComment(false);
                    setNewComment('');
                  }}
                >
                  Cancel
                </Button>
              </Box>
            </>
          ) : null
        ) : !user ? (
          <LoginPromptAlert
            from={location.pathname}
            message="to comment or vote."
            sx={{ mb: 3 }}
          />
        ) : (
          <VerifyEmailPromptAlert
            message="to comment or vote."
            sx={{ mb: 3 }}
          />
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h6">Comments ({totalComments})</Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <CommentSearchBar value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
            <Select
              size="small"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <MenuItem value="newest">Newest</MenuItem>
              <MenuItem value="oldest">Oldest</MenuItem>
              <MenuItem value="upvotes">Most Upvoted</MenuItem>
            </Select>
          </Box>
        </Box>
        {sortedComments.length > 0 ? (
          <InfiniteScroll
            dataLength={Math.min(visibleCount, sortedComments.length)}
            next={fetchMoreComments}
            hasMore={visibleCount < sortedComments.length}
            loader={
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                <CircularProgress />
              </Box>
            }
          >
            {sortedComments.slice(0, visibleCount).map((c, idx) => (
              <React.Fragment key={c.id}>
                <Box sx={{ mb: 0 }}>
                  <ReplyThread
                    reply={c}
                    user={user}
                    verifiedUser={verifiedUser}
                    replyingTo={replyingTo}
                    setReplyingTo={setReplyingTo}
                    replyContent={replyContent}
                    setReplyContent={setReplyContent}
                    onAddReply={handleAddComment}
                    onEdit={setEditCommentData}
                    onDelete={setDeleteCommentData}
                    onUpvote={handleUpvote}
                    onDownvote={handleDownvote}
                    onLock={(comment) =>
                      comment.unlock ? handleUnlockComment(comment) : setLockCommentData(comment)
                    }
                    onRemove={(comment) => setRemoveCommentData(comment)}
                    threadDeleted={threadDeleted}
                    threadLocked={threadLocked}
                    threadRemoved={threadRemoved}
                    expanded={!!expandedComments[c.id]}
                    onToggle={() => toggleExpanded(c.id)}
                  />
                </Box>
              </React.Fragment>
            ))}
          </InfiniteScroll>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No comments yet. Be the first to comment!
          </Typography>
        )}
        </Box>

        {!isSmallScreen && thread.topic && (
          <RightSidebarContainer sx={{ ml: 3, mt: 6 }}>
            {verifiedUser && (
              <Button
                component={RouterLink}
                to={`/community/user/${encodeURIComponent(
                  getUserDisplayName(user)
                )}`}
                variant="outlined"
                size="large"
                sx={{ width: '100%', mb: 1 }}
              >
                View My Posts
              </Button>
            )}
            {verifiedUser ? (
              <FilledButton
                onClick={() => setPostOpen(true)}
                text="Add a Post"
                color="success"
                size="large"
                sx={{ width: '100%', mb: 2 }}
                data-tour-id="community-add-post"
              />
            ) : !user ? (
              <LoginPromptAlert
                from={location.pathname}
                message="to Add a Post."
                sx={{ width: '100%', mb: 2 }}
              />
            ) : (
              <VerifyEmailPromptAlert
                message="to Add a Post."
                sx={{ width: '100%', mb: 2 }}
              />
            )}
            {relatedThreads.length > 0 && (
              <ThreadListSidebar
                title={`More on ${thread.subtopic.name}`}
                threads={relatedThreads}
                currentThreadId={thread.id}
                Icon={FeedIcon}
              />
            )}
            {userThreads.length > 0 && (
              <ThreadListSidebar
                title={`More by ${thread.created_by.display_name || thread.created_by.username}`}
                threads={userThreads}
                currentThreadId={thread.id}
                Icon={FaceIcon}
              />
        )}
      </RightSidebarContainer>
      )}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Delete Thread?</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this thread?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteThread} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
      <EditThreadDialog
        open={editOpen}
        threadId={thread.id}
        onClose={() => setEditOpen(false)}
        onUpdated={res => {
          const updated = res.data || res;
          setThreadData(prev => ({ ...prev, thread: { ...prev.thread, ...updated } }));
        }}
      />
      <Dialog open={!!deleteCommentData} onClose={() => setDeleteCommentData(null)}>
        <DialogTitle>Delete Comment?</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this comment?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteCommentData(null)}>Cancel</Button>
          <Button onClick={handleDeleteComment} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
      <EditCommentDialog
        open={!!editCommentData}
        comment={editCommentData}
        onClose={() => setEditCommentData(null)}
        onUpdated={(content) => editCommentData && handleCommentUpdated(editCommentData.id, content)}
      />
      <ActionReasonDialog
        open={lockThreadOpen}
        onClose={() => setLockThreadOpen(false)}
        title="Lock Thread"
        reasons={POST_LOCK_REASONS}
        onConfirm={handleLockThreadConfirm}
      />
      <ActionReasonDialog
        open={removeThreadOpen}
        onClose={() => setRemoveThreadOpen(false)}
        title="Remove Thread"
        reasons={POST_ADMIN_DECISION_REASONS}
        onConfirm={handleRemoveThreadConfirm}
      />
      <ActionReasonDialog
        open={reportThreadOpen}
        onClose={() => setReportThreadOpen(false)}
        title="Report Thread"
        reasons={POST_REPORT_REASONS}
        onConfirm={handleReportThreadConfirm}
      />
      <ActionReasonDialog
        open={!!lockCommentData}
        onClose={() => setLockCommentData(null)}
        title="Lock Comment"
        reasons={POST_LOCK_REASONS}
        onConfirm={handleLockCommentConfirm}
      />
      <ActionReasonDialog
        open={!!removeCommentData}
        onClose={() => setRemoveCommentData(null)}
        title="Remove Comment"
        reasons={POST_ADMIN_DECISION_REASONS}
        onConfirm={handleRemoveCommentConfirm}
      />
      <AddPostDialog
        open={postOpen}
        onClose={() => setPostOpen(false)}
        previousPath={location.pathname}
        onSuccess={(msg) => setSuccessMessage(msg)}
        onError={(msg) => setErrorMessage(msg)}
      />
      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSuccessMessage('')}
          severity="success"
          sx={{ width: '100%', fontWeight: 'bold' }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={4000}
        onClose={() => setErrorMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setErrorMessage('')}
          severity="error"
          sx={{ width: '100%' }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
      </Box>
    </Box>
  );
}
